import React from "react";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import ReactTable from "react-table";
import moment from "moment";
import {Card, CardContent, Tooltip} from "@material-ui/core";
import ReportDateToolbar, {
	buildReportApiFilter,
	createDefaultReportDateState,
	defaultReportFilterState,
	formatDateRangeLabel,
	formatReportMoney,
} from "components/Elements/ReportFilterToggles";

const DATE_STORAGE_KEY = "dateSalesByCategory";

const ITEMS_SOLD_HEADER_TOOLTIP =
	"Register sales count whole items. Partial layaway, work order, or invoice payments count a share of the items based on how much was paid in this period, so decimals (for example 1.5) are normal.";

const ItemsSoldHeader = () => (
	<Tooltip title={ITEMS_SOLD_HEADER_TOOLTIP}>
		<span
			style={{
				cursor: "help",
				borderBottom: "1px dotted currentColor",
			}}
		>
			Items Sold
		</span>
	</Tooltip>
);

/**
 * Sales by Category report.
 *
 * Canonical template for NEW report Collections — copy this file when adding reports that need
 * the standard header bar. See defaultReportFilterState in ReportFilterToggles.js for the
 * full setup checklist (layaway mode, WO mode, date range, Local/Server TZ).
 */
export default class SalesByCategoryReport extends Framework.Components.Collection {
	constructor(props) {
		let model = new API.BuildClass(Types.Locations_Sales._name, API.RequestType.READ_ALL);
		super(props, model);
		this.request = "GET_CATEGORY_SALES_REPORT";
		Object.assign(this.state, defaultReportFilterState, createDefaultReportDateState(DATE_STORAGE_KEY));
	}

	handleLayawayModeChange = async (completedLayawaysOnly) => {
		await this.setState({completedLayawaysOnly});
		await this.getReport();
	};

	handleWorkOrderModeChange = async (completedWorkOrdersOnly) => {
		await this.setState({completedWorkOrdersOnly});
		await this.getReport();
	};

	handleTimezoneModeChange = async (useLocalTimezone) => {
		await this.setState({useLocalTimezone});
		await this.getReport();
	};

	async componentDidMount() {
		await this.getReport();
	}

	handleToggleModal = (modal) => {
		this.setState({[modal]: !this.state[modal]});
	};

	handleDateChange = async (name, date) => {
		await this.setState({filterDate: moment(date).format()});
		this.handleToggleModal("showDate");
		await this.getReport();
	};

	handleCancelDateRange = () => {
		const today = moment().format("YYYY-MM-DD");
		this.setState({start_date: today, end_date: today});
		this.handleToggleModal("showDateRange");
	};

	buildTableData = (items, totals) => {
		const rows = Array.isArray(items) ? [...items] : [];
		if (totals && rows.length > 0) {
			rows.push({
				total_key: true,
				category_name: "Totals",
				items_sold: totals.items_sold,
				revenue: totals.revenue,
				net_profit: totals.net_profit,
				pct_of_revenue: "100.00",
			});
		}
		return rows;
	};

	getReport = async () => {
		await this.setState({totals: null, loading: true});
		try {
			let model = new API.BuildClass(Types.Locations_Sales._name, API.RequestType.READ);
			this.read = this.request;
			model.filter = buildReportApiFilter(this.state);
			this.state.filter = model.filter;
			const dateRangeFormatted = formatDateRangeLabel(
				this.state.start_date,
				this.state.end_date
			);
			model.search = this.state.search;
			const response = await model.submit(null, this.read);
			const items = response.items || [];
			const totals = response.totals || null;
			const tableData = this.buildTableData(items, totals);
			await this.setState({
				data: tableData,
				reportItems: items,
				totals,
				dateRangeFormatted,
				key: Math.random(),
			});
			localStorage.setItem(DATE_STORAGE_KEY, this.state.filterDate);
		} finally {
			await this.setState({loading: false});
		}
	};

	render() {
		const {data, pages, loading, limit} = this.state;
		const columns = [
			{
				Header: "Category",
				id: "category_name",
				accessor: (r) => r?.category_name ?? "",
				sortable: false,
			},
			{
				Header: ItemsSoldHeader,
				id: "items_sold",
				accessor: (r) => parseFloat(r?.items_sold ?? 0).toFixed(2),
				sortable: false,
			},
			{
				Header: "Revenue",
				id: "revenue",
				accessor: (r) => "$" + formatReportMoney(r?.revenue ?? "0.00"),
				sortable: false,
			},
			{
				Header: "Net Profit",
				id: "net_profit",
				accessor: (r) => "$" + formatReportMoney(r?.net_profit ?? "0.00"),
				sortable: false,
			},
			{
				Header: "% of Total Revenue",
				id: "pct_of_revenue",
				accessor: (r) => {
					if (r?.total_key) {
						return "";
					}
					const pct = r?.pct_of_revenue;
					return pct != null ? `${parseFloat(pct).toFixed(2)}%` : "";
				},
				sortable: false,
			},
		];

		const hasData = data && data.length > 0 && !data.every((r) => r?.total_key);

		return (
			<Card>
				<CardContent>
					<div className="table-bar">
						<ReportDateToolbar
							filterDate={this.state.filterDate}
							start_date={this.state.start_date}
							end_date={this.state.end_date}
							dateRangeFormatted={this.state.dateRangeFormatted}
							showDate={this.state.showDate}
							showStartDate={this.state.showStartDate}
							showEndDate={this.state.showEndDate}
							showDateRange={this.state.showDateRange}
							onToggleModal={this.handleToggleModal}
							onSingleDateChange={this.handleDateChange}
							onStartDateChange={(start_date) => this.setState({start_date})}
							onEndDateChange={(end_date) => this.setState({end_date})}
							onApplyDateRange={this.getReport}
							onCancelDateRange={this.handleCancelDateRange}
							toggleClose={this.toggleClose}
							completedLayawaysOnly={this.state.completedLayawaysOnly}
							completedWorkOrdersOnly={this.state.completedWorkOrdersOnly}
							useLocalTimezone={this.state.useLocalTimezone}
							onLayawayModeChange={this.handleLayawayModeChange}
							onWorkOrderModeChange={this.handleWorkOrderModeChange}
							onTimezoneModeChange={this.handleTimezoneModeChange}
						/>
						<div className="d-flex" style={{paddingTop: 15}}>
							{this.renderHeaderRight("Sales by Category", columns)}
						</div>
					</div>
					{!loading && !hasData ? (
						<div>There is no data for the dates selected.</div>
					) : (
						<ReactTable
							key={this.state.key}
							columns={columns}
							data={data || []}
							pages={pages}
							loading={loading}
							loadingText="Requesting Data"
							getTrProps={(state, rowInfo) => {
								if (rowInfo && rowInfo.row && rowInfo.row._original?.total_key) {
									return {style: {fontWeight: "bold"}};
								}
								return {};
							}}
							defaultSorted={[
								{
									id: this.model.sort,
									desc: this.model.order === "ascending",
								},
							]}
							className="-striped -highlight"
							showPaginationTop={false}
							showPaginationBottom={false}
							showPageSizeOptions={true}
							defaultPageSize={limit || 50}
						/>
					)}
					<Framework.Elements.LoadingSpinner isLoading={loading} />
				</CardContent>
			</Card>
		);
	}
}
